import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useBranchQuery,
  useBranchesQuery,
  useUpdateBranch,
} from "@/modules/branches/presentation/api/queries";
import { BranchRadiusEditor } from "@/modules/branches/presentation/ui/branch-map";
import { branchHasCoordinates } from "@/modules/branches/presentation/ui/branch-map/map-geo-utils";
import type { BranchMapPoint } from "@/modules/branches/presentation/ui/branch-map/BranchMap";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";


import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export function BranchesMapPage() {
  const nav = useNavigate({ from: "/app/branches/map" });
  const search = useSearch({ from: "/app/branches/map" }) as {
    branch?: string;
  };

  const branchesQuery = useBranchesQuery({ limit: 200, offset: 0 });
  const branches = branchesQuery.data?.data ?? [];

  const mappableBranches = useMemo(
    (): BranchMapPoint[] =>
      branches.filter((b) => branchHasCoordinates(b.lat, b.lng)),
    [branches],
  );

  const [selectedId, setSelectedId] = useState<string | null>(
    search.branch ?? null,
  );
  const [radiusM, setRadiusM] = useState(0);

  const selectedBranch = useMemo(
    () => mappableBranches.find((b) => b.id === selectedId) ?? null,
    [mappableBranches, selectedId],
  );

  const branchDetailQuery = useBranchQuery(selectedId ?? "");
  const updateBranch = useUpdateBranch(selectedId ?? "");

  useEffect(() => {
    if (search.branch) setSelectedId(search.branch);
  }, [search.branch]);

  useEffect(() => {
    if (selectedBranch) {
      setRadiusM(selectedBranch.serviceRadiusM ?? 0);
    }
  }, [selectedBranch]);

  const handleSelectBranch = (branch: BranchMapPoint) => {
    setSelectedId(branch.id);
    nav({
      to: "/app/branches/map",
      search: { branch: branch.id },
    });
  };

  const handleSaveRadius = async () => {
    if (!selectedId || !branchDetailQuery.data) return;
    const detail = branchDetailQuery.data;
    try {
      await updateBranch.mutateAsync({
        code: detail.code,
        name: detail.name,
        phone: detail.phone,
        address: detail.address,
        lat: detail.lat,
        lng: detail.lng,
        serviceRadiusM: radiusM,
        timezone: detail.timezone,
        openHoursJson: detail.openHoursJson ?? {},
        isActive: detail.isActive,
      });
      toast.success("ບັນທຶກລັດສະໝີສຳເລັດ");
    } catch {
      toast.error("ບັນທຶກລັດສະໝີບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ສາຂາ", to: "/app/branches" },
            { label: "ແຜນທີ່ສາຂາ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => nav({ to: "/app/branches" })}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ແຜນທີ່ສາຂາ</h2>
            <p className="text-muted-foreground text-sm">
              ແຜນທີ່ mapcn — ເບິ່ງສາຂາ ແລະ ແກ້ລັດສະໝີບໍລິການ
            </p>
          </div>
        </div>

        <QueryState
          isLoading={branchesQuery.isLoading}
          isError={branchesQuery.isError}
          error={branchesQuery.error}
          isEmpty={!branchesQuery.isLoading && mappableBranches.length === 0}
          description="ຍັງບໍ່ມີສາຂາທີ່ມີພິກັດ lat/lng — ເພີ່ມໃນຟອມສາຂາກ່ອນ"
        >
          <BranchRadiusEditor
            branch={selectedBranch}
            radiusM={radiusM}
            onRadiusChange={setRadiusM}
            onSave={handleSaveRadius}
            saving={updateBranch.isPending}
            allBranches={mappableBranches}
            onSelectBranch={handleSelectBranch}
          />
        </QueryState>
      </Main>
    </>
  );
}
